import { portalApi } from 'store/generated/portal/employee/employees';
import { UserContextApiTags } from '../../../../common/api/userContextApi';
import { DebriefScheduleApiTags } from 'areas/assessment-schedule/api/debriefScheduleApi';

export const EmployeeApiTags = {
  Employee: 'Employee',
} as const;

const taggedApi = portalApi.enhanceEndpoints({ addTagTypes: Object.values(EmployeeApiTags) });

const employeeApi = taggedApi.enhanceEndpoints({
  addTagTypes: [
    EmployeeApiTags.Employee,
    UserContextApiTags.UserContext,
    UserContextApiTags.OrganizationContext,
    DebriefScheduleApiTags.DebriefSchedules,
  ],
  endpoints: {
    apiPortalV1EmployeeEmployeesDemographicFieldsRetrieve: {
      providesTags: [EmployeeApiTags.Employee],
    },
    apiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieve: {
      providesTags: (result) =>
        result
          ? [
            ...result.map((m: { id: number }) => ({ type: EmployeeApiTags.Employee, id: m.id })),
            EmployeeApiTags.Employee,
          ]
          : [EmployeeApiTags.Employee],
    },
    apiPortalV1EmployeeEmployeesCreate: {
      invalidatesTags: [
        EmployeeApiTags.Employee,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
        DebriefScheduleApiTags.DebriefSchedules,
      ],
    },
    apiPortalV1EmployeeEmployeesRetrieve: {
      providesTags: (result, error, arg) => [
        { type: EmployeeApiTags.Employee, id: arg.id },
      ],
    },
    apiPortalV1EmployeeEmployeesPartialUpdate: {
      invalidatesTags: (result, error, arg) => [
        { type: EmployeeApiTags.Employee, id: arg.id },
        EmployeeApiTags.Employee,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
        DebriefScheduleApiTags.DebriefSchedules,
      ],
    },
    apiPortalV1EmployeeEmployeesToggleStatusCreate: {
      invalidatesTags: (result, error, arg) => [
        { type: EmployeeApiTags.Employee, id: arg.id },
        EmployeeApiTags.Employee,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
        DebriefScheduleApiTags.DebriefSchedules,
      ],
    },

  },
});

const employeeApiWithDelete = employeeApi.injectEndpoints({
  endpoints: (build) => ({
    deleteMember: build.mutation<any, { id: number; pk: number }>({
      query: ({ id }) => ({
        url: `/api/portal/v1/employee/employees/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: EmployeeApiTags.Employee, id: arg.id },
        EmployeeApiTags.Employee,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
        DebriefScheduleApiTags.DebriefSchedules,
      ],
    }),
    getRosterReview: build.query<any, { organizationId: number }>({
      query: ({ organizationId }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/',
        method: 'GET',
        params: {
          organization_id: organizationId,
        },
      }),
      providesTags: [EmployeeApiTags.Employee],
    }),
    getRosterReviewTeams: build.query<any, { organizationId: number }>({
      query: ({ organizationId }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/teams/',
        method: 'GET',
        params: {
          organization_id: organizationId,
        },
      }),
      providesTags: [EmployeeApiTags.Employee],
    }),
    updateRosterMemberTeams: build.mutation<any, { organizationId: number; employeeId: number; teamIds: number[] }>({
      query: ({ organizationId, employeeId, teamIds }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/update-teams/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          employee_id: employeeId,
          team_ids: teamIds,
        },
      }),
      invalidatesTags: [EmployeeApiTags.Employee],
    }),
    verifyRosterMembers: build.mutation<any, { organizationId: number; employeeIds: number[] }>({
      query: ({ organizationId, employeeIds }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/verify/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          employee_ids: employeeIds,
        },
      }),
      invalidatesTags: [EmployeeApiTags.Employee],
    }),
    unverifyRosterMembers: build.mutation<any, { organizationId: number; employeeIds: number[] }>({
      query: ({ organizationId, employeeIds }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/unverify/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          employee_ids: employeeIds,
        },
      }),
      invalidatesTags: [EmployeeApiTags.Employee],
    }),
    archiveRosterMembers: build.mutation<any, { organizationId: number; employeeIds: number[]; departureDate?: string | null; departureReason?: string | null }>({
      query: ({ organizationId, employeeIds, departureDate, departureReason }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/archive/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          employee_ids: employeeIds,
          departure_date: departureDate ?? null,
          departure_reason: departureReason ?? null,
        },
      }),
      invalidatesTags: [EmployeeApiTags.Employee],
    }),
    restoreRosterMembers: build.mutation<any, { organizationId: number; employeeIds: number[] }>({
      query: ({ organizationId, employeeIds }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/restore/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          employee_ids: employeeIds,
        },
      }),
      invalidatesTags: [EmployeeApiTags.Employee],
    }),
    bulkUpdateRosterCategory: build.mutation<any, { organizationId: number; employeeIds: number[]; fieldName: string; value: string }>({
      query: ({ organizationId, employeeIds, fieldName, value }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/bulk-update-category/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          employee_ids: employeeIds,
          field_name: fieldName,
          value,
        },
      }),
      invalidatesTags: [EmployeeApiTags.Employee],
    }),
    completeRosterReview: build.mutation<any, { organizationId: number; note?: string }>({
      query: ({ organizationId, note }) => ({
        url: '/api/portal/v1/employee/employees/roster-review/complete/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          confirmation_statement: true,
          note: note ?? '',
        },
      }),
      invalidatesTags: [EmployeeApiTags.Employee],
    }),
  }),
  overrideExisting: false,
});

export { employeeApiWithDelete as employeeApi };
